import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaGithub, FaTwitter, FaLinkedin, FaHeart, FaRss } from 'react-icons/fa';
import { HiLightningBolt, HiCollection, HiChatAlt2, HiUsers } from 'react-icons/hi';
import NewsletterSubscription from './NewsletterSubscription';

function Footer() {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'GitHub',
      icon: <FaGithub className="w-5 h-5" />,
      href: 'https://github.com/OmkarNarayanBhal',
      color: 'hover:text-gray-900 dark:hover:text-white'
    },
    // {
    //   name: 'Twitter',
    //   icon: <FaTwitter className="w-5 h-5" />,
    //   href: 'https://twitter.com/yourusername',
    //   color: 'hover:text-blue-400'
    // },
    {
      name: 'LinkedIn',
      icon: <FaLinkedin className="w-5 h-5" />,
      href: 'https://www.linkedin.com/in/omkar-narayan-bhal-aa540b248',
      color: 'hover:text-blue-600'
    },
    {
      name: 'RSS',
      icon: <FaRss className="w-5 h-5" />,
      href: '/rss',
      color: 'hover:text-orange-500'
    }
  ];

  const features = [
    { icon: <HiLightningBolt className="w-6 h-6" />, text: 'Lightning fast performance' },
    { icon: <HiCollection className="w-6 h-6" />, text: 'Rich content library' },
    { icon: <HiChatAlt2 className="w-6 h-6" />, text: 'Active community' },
    { icon: <HiUsers className="w-6 h-6" />, text: 'Expert contributors' }
  ];

  return (
    <footer className="footer-background mt-20">
      <div className="footer-grid" />
      <div className="footer-overlay" />
      
      <div className="relative z-10">
        {/* Features Section */}
        <div className="container mx-auto px-4 py-12 border-b border-gray-200/50 dark:border-gray-700/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex flex-col items-center text-center"
              >
                <div className="p-3 bg-primary-500/10 dark:bg-primary-400/5 rounded-full mb-4 
                              backdrop-blur-sm ring-1 ring-primary-500/20 dark:ring-primary-400/10">
                  <div className="text-primary-500 dark:text-primary-400">
                    {feature.icon}
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300">{feature.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-12 md:gap-8">
            {/* Brand Section */}
            <div className="col-span-1 md:col-span-2">
              <Link 
                to="/" 
                className="inline-block text-2xl font-bold bg-gradient-to-r from-primary-500 to-secondary-500 
                         dark:from-primary-400 dark:to-secondary-400 bg-clip-text text-transparent"
              >
                InkWord
              </Link>
              <p className="mt-4 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                Discover stories, thinking, and expertise from writers on any topic. Join our community of curious minds.
              </p>
              <div className="mt-6 flex space-x-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-gray-500 dark:text-gray-400 transition-all duration-200 ${social.color} 
                             hover:shadow-lg dark:hover:shadow-primary-400/20 p-2 rounded-full 
                             hover:bg-gray-100 dark:hover:bg-gray-800/50`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.name}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-span-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                Navigation
              </h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    to="/animated-home"
                    className="text-sm text-gray-600 hover:text-primary-500 dark:text-gray-300 dark:hover:text-primary-300 transition-all duration-200 hover:translate-x-1"
                  >
                    Home
                  </Link>
                </li>
                {['About', 'Contact'].map((item) => (
                  <li key={item}>
                    <Link
                      to={`/${item.toLowerCase()}`}
                      className="text-sm text-gray-600 hover:text-primary-500 dark:text-gray-300 dark:hover:text-primary-300 transition-all duration-200 hover:translate-x-1"
                    >
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div className="col-span-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                Legal
              </h3>
              <ul className="mt-4 space-y-2">
                {['Privacy Policy', 'Terms', 'Cookie Policy', 'Disclaimer'].map((item) => (
                  <li key={item}>
                    <Link
                      to={`/${item.toLowerCase().replace(' ', '-')}`}
                      className="text-sm text-gray-600 hover:text-primary-500 dark:text-gray-300 dark:hover:text-primary-300 transition-all duration-200 hover:translate-x-1"
                    >
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter Section */}
            <div className="col-span-1 md:col-span-2">
              <NewsletterSubscription />
            </div>
          </div>

          {/* Bottom Section */}
          <div className="mt-12 pt-8 border-t border-gray-200/50 dark:border-gray-700/10">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-sm text-gray-500 dark:text-gray-300">
                © {currentYear} InkWord. All rights reserved.
              </p>
              <div className="mt-4 md:mt-0 flex items-center text-sm text-gray-500 dark:text-gray-300">
                Made with{' '}
                <FaHeart className="w-4 h-4 mx-1 text-red-500/90 dark:text-red-400 animate-pulse" />{' '}
                by{' '}
                <a
                  href="https://omkarportfolio-v2.netlify.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 text-primary-500 hover:text-primary-600 dark:text-primary-400 
                         dark:hover:text-primary-300 transition-colors duration-200 font-medium"
                >
                  Omkar
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer; 